(() => {
  if (window.__dfnAccessoryQuickAddInitialized) return;
  window.__dfnAccessoryQuickAddInitialized = true;

  const openerSelector = '[data-dfn-accessory-quick-add]';
  const productCache = new Map();
  let activeOpener = null;
  let activeProduct = null;
  let activeProductUrl = '';
  let selectedVariant = null;
  let subscriptionSelected = false;
  let selectedSellingPlanId = '';
  let requestController = null;
  let requestId = 0;
  let bodyWasLocked = false;

  const moneyFormatter = () =>
    new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const numberFormatter = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });

  const formatMoney = (cents) =>
    moneyFormatter()
      .format(Number(cents || 0) / 100)
      .replace(/[\s\u00a0\u202f]+€/g, '€');

  const escapeHtml = (value) =>
    String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  function decodeHtml(value) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = String(value ?? '');
    return textarea.value;
  }

  function productJsonUrl(productUrl) {
    const url = new URL(productUrl, window.location.origin);
    url.search = '';
    url.hash = '';
    url.pathname = `${url.pathname.replace(/\/$/, '').replace(/\.js$/, '')}.js`;
    return url.toString();
  }

  function productPageUrl(variantId) {
    const url = new URL(activeProductUrl || activeProduct?.url || '/', window.location.origin);
    url.searchParams.set('variant', String(variantId || ''));
    return `${url.pathname}${url.search}${url.hash}`;
  }

  function embeddedProduct(opener) {
    const node = opener.closest('.quick-add')?.querySelector('[data-dfn-accessory-product-data]');
    if (!node?.textContent?.trim()) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('DFN accessory product data could not be read.', error);
      return null;
    }
  }

  function getModal() {
    let modal = document.querySelector('[data-dfn-accessory-quick-add-modal]');
    if (modal) return modal;

    modal = document.createElement('div');
    modal.className = 'dfn-accessory-quick-add-modal';
    modal.dataset.dfnAccessoryQuickAddModal = '';
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <div class="dfn-accessory-quick-add-modal__backdrop" data-dfn-accessory-modal-close></div>
      <section class="dfn-accessory-quick-add-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="DfnAccessoryQuickAddTitle" tabindex="-1">
        <button type="button" class="dfn-accessory-quick-add-modal__close" data-dfn-accessory-modal-close aria-label="Schließen">
          <span class="dfn-accessory-quick-add-modal__close-icon" aria-hidden="true"></span>
        </button>
        <div class="dfn-accessory-quick-add-modal__body" data-dfn-accessory-modal-body></div>
      </section>
    `;
    document.body.appendChild(modal);

    modal.addEventListener('click', (event) => {
      if (event.target.closest('[data-dfn-accessory-modal-close]')) {
        closeModal();
        return;
      }

      const quantityButton = event.target.closest('[data-dfn-accessory-quantity-change]');
      if (quantityButton) {
        adjustQuantity(modal, Number(quantityButton.dataset.dfnAccessoryQuantityChange || 0));
        return;
      }

      const subscriptionToggle = event.target.closest('[data-dfn-accessory-subscription-toggle]');
      if (subscriptionToggle) {
        toggleSubscription(modal);
        return;
      }

      const sellingPlanButton = event.target.closest('[data-dfn-accessory-selling-plan]');
      if (sellingPlanButton) {
        subscriptionSelected = true;
        selectedSellingPlanId = sellingPlanButton.dataset.sellingPlanId || '';
        renderSubscription(modal, true);
        return;
      }

      const oneTimeButton = event.target.closest('[data-dfn-accessory-one-time]');
      if (oneTimeButton) {
        subscriptionSelected = false;
        selectedSellingPlanId = '';
        renderSubscription(modal, oneTimeButton.hasAttribute('data-dfn-accessory-one-time-option'));
        return;
      }

      const optionButton = event.target.closest('[data-dfn-accessory-select-option]');
      if (optionButton) {
        const group = optionButton.closest('[data-dfn-accessory-option-group]');
        const input = group?.querySelector('[data-dfn-accessory-option]');
        if (!group || !input) return;

        const optionIndex = Number(group.dataset.optionIndex || 0);
        const selectedValue = optionButton.dataset.value || '';
        input.value = selectedValue;
        input.dataset.selectedValue = selectedValue;
        updateSelectedVariant(modal);

        const updatedGroup = [...modal.querySelectorAll('[data-dfn-accessory-option-group]')].find(
          (item) => Number(item.dataset.optionIndex || 0) === optionIndex
        );
        const updatedButton = [...(updatedGroup?.querySelectorAll('[data-dfn-accessory-select-option]') || [])].find(
          (item) => item.dataset.value === selectedValue
        );
        updatedButton?.focus({ preventScroll: true });
      }
    });

    modal.addEventListener('keydown', handleSelectKeyboard);
    modal.addEventListener('change', (event) => {
      if (event.target.matches('[data-dfn-accessory-quantity-input]')) syncQuantity(modal, false);
    });
    modal.addEventListener('submit', handleAddToCart);
    return modal;
  }

  function setOpenerLoading(opener, loading) {
    if (!opener) return;
    opener.classList.toggle('loading', loading);
    opener.toggleAttribute('aria-disabled', loading);
    opener.disabled = loading;
    opener.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
  }

  function openModalShell(opener) {
    const modal = getModal();
    activeOpener = opener;
    bodyWasLocked = document.body.classList.contains('overflow-hidden');
    document.body.classList.add('overflow-hidden');
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    modal.querySelector('[data-dfn-accessory-modal-body]').innerHTML = `
      <div class="dfn-accessory-quick-add-modal__loading" role="status" aria-live="polite">
        <span class="dfn-accessory-quick-add-modal__loader" aria-hidden="true"></span>
        <span>Produktoptionen werden geladen …</span>
      </div>
    `;
    requestAnimationFrame(() => {
      modal.classList.add('is-open');
      modal.querySelector('.dfn-accessory-quick-add-modal__dialog')?.focus({ preventScroll: true });
    });
    return modal;
  }

  function closeModal() {
    const modal = document.querySelector('[data-dfn-accessory-quick-add-modal]');
    if (!modal || modal.hidden) return;

    requestController?.abort();
    requestController = null;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    if (!bodyWasLocked) document.body.classList.remove('overflow-hidden');

    window.setTimeout(() => {
      modal.hidden = true;
      modal.querySelector('[data-dfn-accessory-modal-body]').replaceChildren();
    }, 180);

    const opener = activeOpener;
    activeOpener = null;
    activeProduct = null;
    activeProductUrl = '';
    selectedVariant = null;
    subscriptionSelected = false;
    selectedSellingPlanId = '';
    opener?.focus({ preventScroll: true });
  }

  function optionNames(product) {
    return (product.options || []).map((option, index) => {
      if (typeof option === 'string') return option;
      return option?.name || `Option ${index + 1}`;
    });
  }

  function variantOptions(variant) {
    if (Array.isArray(variant.options)) return variant.options.map(String);
    return [variant.option1, variant.option2, variant.option3].filter((value) => value != null).map(String);
  }

  function availableVariants(product) {
    return (product.variants || []).filter((variant) => variant.available !== false);
  }

  function imageUrl(product, variant) {
    const image = variant?.featured_image?.src || variant?.featured_image || product.featured_image;
    if (typeof image === 'string') return image;
    return image?.src || '';
  }

  function effectiveComparePrice(variant) {
    return Math.max(Number(variant?.compare_at_price || 0), Number(variant?.uvp || 0));
  }

  function unitBase(measurement) {
    if (!measurement) return '';
    const value = Number(measurement.reference_value || 1);
    const rawUnit = String(measurement.reference_unit || '').toLowerCase();
    const units = { kg: 'Kg', g: 'g', l: 'L', ml: 'ml', item: 'Stück' };
    return `${value !== 1 ? numberFormatter.format(value) : ''}${units[rawUnit] || measurement.reference_unit || ''}`;
  }

  function priceMarkup(variant) {
    if (!variant) return '';

    const price = Number(variant.price || 0);
    const comparePrice = effectiveComparePrice(variant);
    const onSale = comparePrice > price;
    const saving = onSale ? comparePrice - price : 0;
    const savingPercent = onSale ? Math.round((saving * 100) / comparePrice) : 0;
    const measurement = variant.unit_price_measurement;
    const unitPrice = Number(variant.unit_price || 0);
    const unit = measurement && unitPrice > 0 ? unitBase(measurement) : '';

    return `
      <div class="dfn-accessory-quick-add-modal__price-main">
        <span class="visually-hidden">Unser Preis:</span>
        <span class="dfn-accessory-quick-add-modal__price-current">${escapeHtml(formatMoney(price))}</span>
        ${
          onSale
            ? `<span class="dfn-accessory-quick-add-modal__price-compare"><span>UVP</span><s>${escapeHtml(
                formatMoney(comparePrice)
              )}</s></span>
               <span class="dfn-accessory-quick-add-modal__price-saving">Du sparst ${escapeHtml(
                 formatMoney(saving)
               )} (${savingPercent}%)</span>`
            : ''
        }
      </div>
      ${
        unit
          ? `<div class="dfn-accessory-quick-add-modal__unit-price">${escapeHtml(formatMoney(unitPrice))} / ${escapeHtml(unit)}</div>`
          : ''
      }
    `;
  }

  function normalizeRuleKey(value) {
    return String(value ?? '')
      .toLowerCase()
      .trim()
      .replaceAll('ä', 'ae')
      .replaceAll('ö', 'oe')
      .replaceAll('ü', 'ue')
      .replaceAll('ß', 'ss')
      .replace(/[\s\-_.\/&()]/g, '');
  }

  function ruleValueMatches(left, right) {
    const leftKey = normalizeRuleKey(left);
    const rightKey = normalizeRuleKey(right);
    if (!leftKey || !rightKey) return false;
    return leftKey === rightKey || leftKey.includes(rightKey) || rightKey.includes(leftKey);
  }

  function bestsellerBadge(product, optionIndex, value, chosenOptions) {
    const rules = Array.isArray(product?.bestseller_rules) ? product.bestseller_rules : [];
    if (!rules.length) return '';

    const names = optionNames(product);
    const optionName = names[optionIndex] || '';
    const contextualOptions = [...chosenOptions];
    contextualOptions[optionIndex] = value;

    const matchingRule = rules.find((rule) => {
      const targetName = rule?.targetOptionName || '';
      const targetValue = rule?.targetOptionValue || '';
      const targetNameMatches = !normalizeRuleKey(targetName) || normalizeRuleKey(targetName) === normalizeRuleKey(optionName);
      if (!targetNameMatches || !ruleValueMatches(value, targetValue)) return false;

      const conditionName = rule?.conditionOptionName || '';
      const conditionValue = rule?.conditionOptionValue || '';
      if (!normalizeRuleKey(conditionName) && !normalizeRuleKey(conditionValue)) return true;

      let conditionIndex = 0;
      if (normalizeRuleKey(conditionName)) {
        conditionIndex = names.findIndex((name) => normalizeRuleKey(name) === normalizeRuleKey(conditionName));
        if (conditionIndex < 0) return false;
      }

      return !normalizeRuleKey(conditionValue) || ruleValueMatches(contextualOptions[conditionIndex], conditionValue);
    });

    return matchingRule?.badgeText || (matchingRule ? 'Bestseller' : '');
  }


  function quantityRule(variant) {
    const rule = variant?.quantity_rule || {};
    const min = Math.max(1, Number(rule.min || 1));
    const increment = Math.max(1, Number(rule.increment || 1));
    const maxValue = Number(rule.max);
    const max = Number.isFinite(maxValue) && maxValue > 0 ? maxValue : null;
    return { min, max, increment };
  }

  function clampQuantity(value, rule) {
    let next = Number(value);
    if (!Number.isFinite(next)) next = rule.min;
    next = Math.max(rule.min, next);
    const steps = Math.round((next - rule.min) / rule.increment);
    next = rule.min + Math.max(0, steps) * rule.increment;
    if (rule.max != null) next = Math.min(rule.max, next);
    return next;
  }

  function syncQuantity(modal, reset = false) {
    const input = modal.querySelector('[data-dfn-accessory-quantity-input]');
    if (!input || !selectedVariant) return;

    const rule = quantityRule(selectedVariant);
    input.min = String(rule.min);
    input.step = String(rule.increment);
    if (rule.max != null) input.max = String(rule.max);
    else input.removeAttribute('max');

    input.value = String(clampQuantity(reset ? rule.min : input.value, rule));
    const minus = modal.querySelector('[data-dfn-accessory-quantity-change="-1"]');
    const plus = modal.querySelector('[data-dfn-accessory-quantity-change="1"]');
    if (minus) minus.disabled = Number(input.value) <= rule.min;
    if (plus) plus.disabled = rule.max != null && Number(input.value) >= rule.max;
  }

  function adjustQuantity(modal, direction) {
    const input = modal.querySelector('[data-dfn-accessory-quantity-input]');
    if (!input || !selectedVariant) return;
    const rule = quantityRule(selectedVariant);
    input.value = String(clampQuantity(Number(input.value || rule.min) + direction * rule.increment, rule));
    syncQuantity(modal, false);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function sellingPlanAllocations(variant) {
    return Array.isArray(variant?.selling_plan_allocations) ? variant.selling_plan_allocations.filter((item) => item?.id) : [];
  }

  function subscriptionDiscount(allocation) {
    const price = Number(allocation?.price || 0);
    const compare = Number(allocation?.compare_at_price || selectedVariant?.price || 0);
    return compare > price ? Math.round(((compare - price) * 100) / compare) : 0;
  }

  function germanPlanName(name, index) {
    const raw = decodeHtml(name).replace(/\s+/g, ' ').trim();
    if (!raw) return `Abo-Option ${index + 1}`;

    const months = raw.match(/every\s*(\d+)\s*months?/i);
    if (months) return Number(months[1]) === 1 ? 'Monatlich liefern & sparen' : `Alle ${months[1]} Monate liefern & sparen`;
    if (/month|monthly|subscribe|subscription/i.test(raw)) return 'Monatlich liefern & sparen';
    if (/week|weekly/i.test(raw)) return 'Wöchentliche Lieferung';
    if (/year|yearly|annual/i.test(raw)) return 'Jährliche Lieferung';
    if (/deliver|delivery/i.test(raw)) return 'Regelmäßige Lieferung';
    return raw;
  }

    function renderSubscription(modal, keepOpen = null) {
    const preview = modal.querySelector(
      '[data-dfn-accessory-subscription]'
    );

    const shell = preview?.closest(
      '.dfn-accessory-quick-add-modal__subscription-clone'
    );

    const panel = modal.querySelector(
      '[data-dfn-accessory-subscription-panel]'
    );

    const plans = modal.querySelector(
      '[data-dfn-accessory-selling-plans]'
    );

    const sellingPlanInput = modal.querySelector(
      '[data-dfn-accessory-selling-plan-input]'
    );

    if (!preview || !panel || !plans || !sellingPlanInput) return;

    const allocations = sellingPlanAllocations(selectedVariant);

    if (!allocations.length) {
      preview.hidden = true;

      if (shell) {
        shell.hidden = true;
      }

      panel.hidden = true;
      subscriptionSelected = false;
      selectedSellingPlanId = '';
      sellingPlanInput.disabled = true;
      sellingPlanInput.value = '';

      return;
    }

    preview.hidden = false;

    if (shell) {
      shell.hidden = false;
    }

    let allocation = allocations.find(
      (item) =>
        String(item.id) === String(selectedSellingPlanId)
    );

    if (!allocation) {
      allocation = allocations[0];
    }

    if (subscriptionSelected) {
      selectedSellingPlanId = String(allocation.id);
    }

    const discount =
      subscriptionDiscount(allocation) || 5;

    const regularPrice = Number(
      allocation.compare_at_price ||
      selectedVariant?.price ||
      0
    );

    const subscriptionPrice = Number(
      allocation.price ||
      selectedVariant?.price ||
      0
    );

    const title = preview.querySelector(
      '[data-dfn-accessory-subscription-title]'
    );

    const benefit = preview.querySelector(
      '[data-dfn-accessory-subscription-benefit]'
    );

    const price = preview.querySelector(
      '[data-dfn-accessory-subscription-price]'
    );

    const compare = preview.querySelector(
      '[data-dfn-accessory-subscription-compare]'
    );

    const toggleText = preview.querySelector(
      '[data-dfn-accessory-subscription-button-text]'
    );

    const oneTimeLink = preview.querySelector(
      '[data-dfn-accessory-one-time-link]'
    );

    if (title) {
      title.textContent =
        `Monatlich liefern & ${discount}% sparen`;
    }

    if (benefit) {
      benefit.textContent =
        `${discount}% Rabatt auf jede Lieferung`;
    }

    if (price) {
      price.textContent =
        formatMoney(subscriptionPrice);
    }

    if (compare) {
      compare.textContent =
        regularPrice > subscriptionPrice
          ? `statt ${formatMoney(regularPrice)}`
          : '';
    }

    if (toggleText) {
      toggleText.textContent =
        subscriptionSelected
          ? 'Abo bearbeiten'
          : 'Abo auswählen';
    }

    if (oneTimeLink) {
      oneTimeLink.hidden = !subscriptionSelected;
    }

    sellingPlanInput.disabled =
      !subscriptionSelected;

    sellingPlanInput.value =
      subscriptionSelected
        ? String(allocation.id)
        : '';

    preview.classList.toggle(
      'is-dfn-subscription-selected',
      subscriptionSelected
    );

    const planButtons = allocations
      .map((item, index) => {
        const selected =
          subscriptionSelected &&
          String(item.id) === String(allocation.id);

        const itemDiscount =
          subscriptionDiscount(item) || discount;

        const itemCompare = Number(
          item.compare_at_price ||
          selectedVariant?.price ||
          0
        );

        const itemPrice = Number(
          item.price || 0
        );

        return `
          <button
            type="button"
            class="bs-item dfn-native-subscription-option"
            data-dfn-accessory-selling-plan
            data-selling-plan-id="${escapeHtml(item.id)}"
            data-selected="${selected}"
            aria-pressed="${selected}"
            style="
              display: block !important;
              width: 100% !important;
              max-width: none !important;
              box-sizing: border-box !important;
              text-align: left !important;
            "
          >
            <span
              class="bs-stack-item-top"
              style="
                display: grid !important;
                width: 100% !important;
                max-width: none !important;
                min-width: 0 !important;
                grid-template-columns:
                  minmax(0, 1fr)
                  max-content !important;
                align-items: center !important;
                column-gap: 1rem !important;
                text-align: left !important;
              "
            >
              <span
                class="bs-stack-item-left"
                style="
                  display: flex !important;
                  width: auto !important;
                  min-width: 0 !important;
                  max-width: none !important;
                  align-items: center !important;
                  justify-content: flex-start !important;
                  justify-self: stretch !important;
                  margin: 0 !important;
                  padding: 0 !important;
                  text-align: left !important;
                "
              >
                <span
                  class="bs-item-label-text"
                  style="
                    display: block !important;
                    width: 100% !important;
                    min-width: 0 !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    text-align: left !important;
                  "
                >
                  ${escapeHtml(
                    germanPlanName(item.name, index)
                  )}
                </span>
              </span>

              <span
                class="bs-stack-item-right"
                style="
                  display: flex !important;
                  width: max-content !important;
                  min-width: max-content !important;
                  max-width: none !important;
                  align-items: center !important;
                  justify-content: flex-end !important;
                  justify-self: end !important;
                  flex-wrap: nowrap !important;
                  margin: 0 !important;
                  padding: 0 !important;
                  gap: 0.65rem !important;
                  text-align: right !important;
                  white-space: nowrap !important;
                "
              >
                ${
                  itemCompare > itemPrice
                    ? `
                      <span
                        class="bs-item-label-compare-price"
                        style="
                          display: block !important;
                          width: auto !important;
                          margin: 0 !important;
                          padding: 0 !important;
                          text-align: right !important;
                          white-space: nowrap !important;
                        "
                      >
                        ${escapeHtml(
                          formatMoney(itemCompare)
                        )}
                      </span>
                    `
                    : ''
                }

                ${
                  itemDiscount
                    ? `
                      <span
                        class="bs-item-label-sale-badge"
                      >
                        Spare ${itemDiscount} %
                      </span>
                    `
                    : ''
                }

                <span
                  class="bs-item-label-price"
                  style="
                    display: block !important;
                    width: auto !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    text-align: right !important;
                    white-space: nowrap !important;
                  "
                >
                  ${escapeHtml(
                    formatMoney(itemPrice)
                  )}
                </span>
              </span>
            </span>
          </button>
        `;
      })
      .join('');

    plans.innerHTML = `
      ${planButtons}

      <button
        type="button"
        class="bs-item dfn-native-one-time-option"
        data-dfn-accessory-one-time
        data-dfn-accessory-one-time-option
        data-selected="${!subscriptionSelected}"
        aria-pressed="${!subscriptionSelected}"
        style="
          display: block !important;
          width: 100% !important;
          max-width: none !important;
          box-sizing: border-box !important;
          text-align: left !important;
        "
      >
        <span
          class="bs-stack-item-top"
          style="
            display: grid !important;
            width: 100% !important;
            max-width: none !important;
            min-width: 0 !important;
            grid-template-columns:
              minmax(0, 1fr)
              max-content !important;
            align-items: center !important;
            column-gap: 1rem !important;
            text-align: left !important;
          "
        >
          <span
            class="bs-stack-item-left"
            style="
              display: flex !important;
              width: auto !important;
              min-width: 0 !important;
              max-width: none !important;
              align-items: center !important;
              justify-content: flex-start !important;
              justify-self: stretch !important;
              margin: 0 !important;
              padding: 0 !important;
              text-align: left !important;
            "
          >
            <span
              class="bs-item-label-text"
              style="
                display: block !important;
                width: 100% !important;
                min-width: 0 !important;
                margin: 0 !important;
                padding: 0 !important;
                text-align: left !important;
              "
            >
              Einzelkauf
            </span>
          </span>

          <span
            class="bs-stack-item-right"
            style="
              display: flex !important;
              width: max-content !important;
              min-width: max-content !important;
              max-width: none !important;
              align-items: center !important;
              justify-content: flex-end !important;
              justify-self: end !important;
              margin: 0 !important;
              padding: 0 !important;
              text-align: right !important;
              white-space: nowrap !important;
            "
          >
            <span
              class="bs-item-label-price"
              style="
                display: block !important;
                width: auto !important;
                margin: 0 !important;
                padding: 0 !important;
                text-align: right !important;
                white-space: nowrap !important;
              "
            >
              ${escapeHtml(
                formatMoney(
                  selectedVariant?.price || 0
                )
              )}
            </span>
          </span>
        </span>
      </button>
    `;

    if (keepOpen !== null) {
      preview.classList.toggle(
        'is-dfn-subscription-open',
        Boolean(keepOpen)
      );
    }

    const open = preview.classList.contains(
      'is-dfn-subscription-open'
    );

    panel.classList.toggle(
      'is-dfn-subscription-open',
      open
    );

    panel.hidden = !open;

    panel.setAttribute(
      'aria-hidden',
      open ? 'false' : 'true'
    );

    preview
      .querySelector(
        '[data-dfn-accessory-subscription-toggle]'
      )
      ?.setAttribute(
        'aria-expanded',
        open ? 'true' : 'false'
      );
  }

  function toggleSubscription(modal) {
    const section = modal.querySelector('[data-dfn-accessory-subscription]');
    if (!section || section.hidden) return;
    const allocations = sellingPlanAllocations(selectedVariant);
    if (!allocations.length) return;

    if (!subscriptionSelected) {
      subscriptionSelected = true;
      selectedSellingPlanId = String(allocations[0].id);
    }
    renderSubscription(modal, !section.classList.contains('is-dfn-subscription-open'));
  }

  function optionMarkup(product, selectedOptions) {
    const names = optionNames(product);
    const showOptions = names.length > 0 && !(names.length === 1 && selectedOptions[0] === 'Default Title');
    if (!showOptions) return '';

    return names
      .map((name, index) => {
        const labelId = `DfnAccessoryOptionLabel-${product.id || 'product'}-${index}`;

        return `
          <fieldset class="dfn-accessory-quick-add-modal__option" data-dfn-accessory-option-group data-option-index="${index}">
            <legend class="dfn-accessory-quick-add-modal__option-label" id="${labelId}">${escapeHtml(name)}</legend>
            <div
              class="dfn-accessory-quick-add-modal__variant-buttons"
              data-dfn-accessory-option-values
              role="radiogroup"
              aria-labelledby="${labelId}"
            <input
              type="hidden"
              data-dfn-accessory-option
              data-option-index="${index}"
              data-selected-value="${escapeHtml(selectedOptions[index] || '')}"
              value="${escapeHtml(selectedOptions[index] || '')}"
            >
          </fieldset>
        `;
      })
      .join('');
  }

  function handleSelectKeyboard(event) {
    const optionButton = event.target.closest('[data-dfn-accessory-select-option]');
    if (!optionButton) return;

    const group = optionButton.closest('[data-dfn-accessory-option-group]');
    const buttons = [...group.querySelectorAll('[data-dfn-accessory-select-option]')];
    const currentIndex = buttons.indexOf(optionButton);
    let nextIndex = null;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % buttons.length;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = buttons.length - 1;
    if (nextIndex == null) return;

    event.preventDefault();
    buttons[nextIndex]?.click();
  }

  function renderVariantButtons(group, values, currentValue, chosenOptions, optionIndex) {
    const container = group.querySelector('[data-dfn-accessory-option-values]');
    if (!container) return;

    container.dataset.count = String(values.length);
    container.innerHTML = values
      .map((value) => {
        const badge = bestsellerBadge(activeProduct, optionIndex, value, chosenOptions);
        const selected = value === currentValue;
        return `
          <button
            type="button"
            class="dfn-accessory-quick-add-modal__variant-button${selected ? ' is-selected' : ''}"
            data-dfn-accessory-select-option
            data-value="${escapeHtml(value)}"
            role="radio"
            aria-checked="${selected}"
            tabindex="${selected ? '0' : '-1'}"
          >
            <span class="dfn-accessory-quick-add-modal__variant-value">${escapeHtml(value)}</span>
            ${badge ? `<span class="dfn-accessory-quick-add-modal__variant-badge">${escapeHtml(badge)}</span>` : ''}
          </button>
        `;
      })
      .join('');
  }

  function renderProduct(modal, product) {
    activeProduct = product;
    activeProductUrl = product.url || activeOpener?.dataset.productUrl || '';
    subscriptionSelected = false;
    selectedSellingPlanId = '';
    selectedVariant = availableVariants(product)[0] || null;
    const selectedOptions = selectedVariant ? variantOptions(selectedVariant) : [];

    const body = modal.querySelector('[data-dfn-accessory-modal-body]');
    body.innerHTML = `
      <div class="dfn-accessory-quick-add-modal__product">
        <div class="dfn-accessory-quick-add-modal__media">
          <img data-dfn-accessory-modal-image src="${escapeHtml(imageUrl(product, selectedVariant))}" alt="${escapeHtml(product.title)}">
        </div>
        <div class="dfn-accessory-quick-add-modal__details">
          <h2 id="DfnAccessoryQuickAddTitle">${escapeHtml(product.title)}</h2>
          <div class="dfn-accessory-quick-add-modal__price" data-dfn-accessory-modal-price></div>
          <form class="dfn-accessory-quick-add-modal__form" data-dfn-accessory-add-form>
            <div class="dfn-accessory-quick-add-modal__options">${optionMarkup(product, selectedOptions)}</div>
            <div class="dfn-accessory-quick-add-modal__purchase-row">
              <div class="dfn-accessory-quick-add-modal__quantity-wrap">
                <label class="dfn-accessory-quick-add-modal__quantity-label" for="DfnAccessoryQuantity">Anzahl</label>
                <div class="dfn-accessory-quick-add-modal__quantity">
                  <button type="button" data-dfn-accessory-quantity-change="-1" aria-label="Anzahl verringern">−</button>
                  <input id="DfnAccessoryQuantity" type="number" name="quantity" value="1" inputmode="numeric" data-dfn-accessory-quantity-input aria-label="Anzahl">
                  <button type="button" data-dfn-accessory-quantity-change="1" aria-label="Anzahl erhöhen">+</button>
                </div>
              </div>
              <button type="submit" class="dfn-accessory-quick-add-modal__add">
                <span data-dfn-accessory-add-label>In den Warenkorb</span>
                <span class="dfn-accessory-quick-add-modal__add-spinner" aria-hidden="true"></span>
              </button>
            </div>
            <div class="dfn-accessory-quick-add-modal__subscription-clone dfn-product-page">
              <section class="dfn-subscription-preview" data-dfn-accessory-subscription hidden aria-label="Abo-Option">
                <div class="dfn-subscription-preview__content">
                  <div class="dfn-subscription-preview__heading">
                    <h3 data-dfn-accessory-subscription-title>Monatlich liefern & sparen</h3>
                    <span class="dfn-subscription-preview__recommendation">Empfohlen</span>
                  </div>
                  <ul class="dfn-subscription-preview__benefits">
                    <li>Regelmäßig & bequem nach Hause</li>
                    <li>Jederzeit kündbar</li>
                    <li data-dfn-accessory-subscription-benefit>Rabatt auf jede Lieferung</li>
                  </ul>
                </div>
                <div class="dfn-subscription-preview__aside">
                  <strong class="dfn-subscription-preview__price" data-dfn-accessory-subscription-price></strong>
                  <span class="dfn-subscription-preview__compare" data-dfn-accessory-subscription-compare></span>
                  <button type="button" class="dfn-subscription-preview__button" data-dfn-accessory-subscription-toggle aria-expanded="false">
                    <span data-dfn-accessory-subscription-button-text>Abo auswählen</span>
                    <svg viewBox="0 0 12 8" aria-hidden="true"><path d="m1 1.5 5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </button>
                </div>
                <button type="button" class="dfn-subscription-preview__one-time" data-dfn-accessory-one-time data-dfn-accessory-one-time-link hidden>
                  Stattdessen Einzelkauf wählen
                </button>
              </section>
              <div class="dfn-native-subscription-panel" data-dfn-accessory-subscription-panel hidden aria-hidden="true">
                <div class="bs-items" data-dfn-accessory-selling-plans></div>
              </div>
            </div>
            <input type="hidden" name="id" data-dfn-accessory-variant-id>
            <input type="hidden" name="selling_plan" data-dfn-accessory-selling-plan-input disabled>
            <p class="dfn-accessory-quick-add-modal__error" data-dfn-accessory-modal-error hidden></p>
            <div class="dfn-accessory-quick-add-modal__actions">
              <a class="dfn-accessory-quick-add-modal__product-link" data-dfn-accessory-product-link href="${escapeHtml(
                activeProductUrl
              )}">Zum Produkt</a>
            </div>
          </form>
        </div>
      </div>
    `;
    updateSelectedVariant(modal);
  }

  function syncOptionSelects(modal) {
    const variants = availableVariants(activeProduct);
    const inputs = [...modal.querySelectorAll('[data-dfn-accessory-option]')].sort(
      (a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex)
    );
    const chosen = inputs.map((input) => input.value || input.dataset.selectedValue || '');
    const valuesByIndex = [];

    inputs.forEach((input, index) => {
      const matchingVariants = variants.filter((variant) => {
        const options = variantOptions(variant);
        return chosen.slice(0, index).every((value, previousIndex) => options[previousIndex] === value);
      });
      const values = [...new Set(matchingVariants.map((variant) => variantOptions(variant)[index]).filter(Boolean))];
      const currentValue = values.includes(chosen[index]) ? chosen[index] : values[0] || '';

      input.value = currentValue;
      input.dataset.selectedValue = currentValue;
      chosen[index] = currentValue;
      valuesByIndex[index] = values;
    });

    inputs.forEach((input, index) => {
      const group = input.closest('[data-dfn-accessory-option-group]');
      if (group) renderVariantButtons(group, valuesByIndex[index] || [], chosen[index] || '', chosen, index);
    });

    return chosen;
  }

  function updateSelectedVariant(modal) {
    if (!activeProduct) return;
    const chosen = syncOptionSelects(modal);
    const variants = availableVariants(activeProduct);

    selectedVariant =
      variants.find((variant) => {
        const options = variantOptions(variant);
        return chosen.every((value, index) => options[index] === value);
      }) || variants[0] || null;

    const variantInput = modal.querySelector('[data-dfn-accessory-variant-id]');
    const price = modal.querySelector('[data-dfn-accessory-modal-price]');
    const image = modal.querySelector('[data-dfn-accessory-modal-image]');
    const addButton = modal.querySelector('.dfn-accessory-quick-add-modal__add');
    const addLabel = modal.querySelector('[data-dfn-accessory-add-label]');
    const productLink = modal.querySelector('[data-dfn-accessory-product-link]');

    if (variantInput) variantInput.value = selectedVariant?.id || '';
    if (price) price.innerHTML = priceMarkup(selectedVariant);
    if (image && selectedVariant) image.src = imageUrl(activeProduct, selectedVariant);
    if (productLink && selectedVariant) productLink.href = productPageUrl(selectedVariant.id);
    syncQuantity(modal, true);
    renderSubscription(modal, false);

    const available = Boolean(selectedVariant?.available);
    if (addButton) addButton.disabled = !available;
    if (addLabel) addLabel.textContent = available ? 'In den Warenkorb' : 'Nicht verfügbar';
  }

  async function loadProduct(opener) {
    const productUrl = opener.dataset.productUrl;
    if (!productUrl) return;

    const modal = openModalShell(opener);
    setOpenerLoading(opener, true);
    const currentRequest = ++requestId;

    try {
      let product = embeddedProduct(opener);
      if (!product) {
        const jsonUrl = productJsonUrl(productUrl);
        product = productCache.get(jsonUrl);
        if (!product) {
          requestController?.abort();
          requestController = new AbortController();
          const response = await fetch(jsonUrl, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal: requestController.signal,
          });
          if (!response.ok) throw new Error(`Produktdaten konnten nicht geladen werden (${response.status}).`);
          product = await response.json();
          productCache.set(jsonUrl, product);
        }
      }

      if (currentRequest !== requestId || modal.hidden) return;
      renderProduct(modal, product);
    } catch (error) {
      if (error.name === 'AbortError') return;
      modal.querySelector('[data-dfn-accessory-modal-body]').innerHTML = `
        <p class="dfn-accessory-quick-add-modal__load-error">Die Produktoptionen konnten leider nicht geladen werden. Bitte versuche es erneut.</p>
      `;
      console.error('DFN accessory quick add:', error);
    } finally {
      setOpenerLoading(opener, false);
    }
  }

  async function handleAddToCart(event) {
    const form = event.target.closest('[data-dfn-accessory-add-form]');
    if (!form) return;
    event.preventDefault();
    event.stopPropagation();

    const button = form.querySelector('.dfn-accessory-quick-add-modal__add');
    const errorNode = form.querySelector('[data-dfn-accessory-modal-error]');
    const variantId = form.querySelector('[name="id"]')?.value;
    if (!variantId || !selectedVariant?.available || button?.disabled) return;

    button.disabled = true;
    button.classList.add('is-loading');
    errorNode.hidden = true;
    errorNode.textContent = '';

    try {
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const formData = new FormData(form);
      if (cart?.getSectionsToRender) {
        formData.append(
          'sections',
          cart
            .getSectionsToRender()
            .map((section) => section.id)
            .join(',')
        );
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement?.(activeOpener || document.activeElement);
      }

      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Das Produkt konnte nicht hinzugefügt werden.');

      if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          data: {
            source: 'dfn-accessory-quick-add',
            productVariantId: variantId,
            cartData: result,
          },
        });
      }

      closeModal();
      if (cart?.renderContents) {
        cart.renderContents(result);
      } else {
        window.location.assign(window.routes?.cart_url || '/cart');
      }
    } catch (error) {
      errorNode.textContent = error.message || 'Das Produkt konnte nicht hinzugefügt werden.';
      errorNode.hidden = false;
    } finally {
      button.classList.remove('is-loading');
      button.disabled = !selectedVariant?.available;
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const opener = event.target.closest(openerSelector);
      if (!opener) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (opener.getAttribute('aria-disabled') === 'true') return;
      loadProduct(opener);
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    const modal = document.querySelector('[data-dfn-accessory-quick-add-modal]');
    if (event.key !== 'Escape' || !modal || modal.hidden) return;

    closeModal();
  });
})();
